import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  SignOut, User, UsersThree, Barbell, ChartLine, Trophy,
  CalendarBlank, House, BookOpen, Gear, CreditCard, Camera, CloudSlash, CloudCheck, ArrowsClockwise,
  CurrencyDollar, CloudArrowDown, IdentificationCard, ImageSquare,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import ProfileDialog from "@/components/app/ProfileDialog";
import BackupDialog from "@/components/app/BackupDialog";
import { isOnline, onStatusChange, flushQueue, queueLength } from "@/lib/offline";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function NavItem({ to, icon: Icon, children, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors",
          isActive
            ? "bg-moss text-white hover:bg-moss-hover hover:text-white"
            : "text-ink-2 hover:bg-muted2 hover:text-ink"
        )
      }
      data-testid={`nav-${String(children).toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={18} weight="regular" />
      <span>{children}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const isCoach = user?.role === "coach";
  const isAthlete = user?.role === "athlete";

  // Layer 1 (in-app modal) is wired to the Sign-out buttons below.
  // Layer 2: the browser-native confirm — fires on tab close / reload / cross-origin
  // nav so an accidental swipe-back doesn't lose an in-progress workout.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      // Chrome requires returnValue to trigger the native prompt.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const confirmLogout = async () => {
    setLogoutOpen(false);
    await logout();
    nav("/login");
  };

  const handleLogout = () => setLogoutOpen(true);

  return (
    <div className="min-h-screen flex">
      {/* Floating sign-out — fixed top-left, visible on every screen so users
          never have to hunt for it. Sized to clear the desktop sidebar logo. */}
      <button
        onClick={handleLogout}
        className="fixed top-3 left-3 z-50 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-ink text-bone text-xs font-semibold shadow-lg hover:bg-ink/90 transition-colors"
        data-testid="floating-logout-button"
        aria-label="Sign out"
        title="Sign out"
      >
        <SignOut size={14} weight="bold" />
        <span className="hidden sm:inline">Sign out</span>
      </button>
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-line bg-surface">
        <div className="pl-24 pr-6 py-6 border-b border-line">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-xl bg-moss grid place-items-center">
              <Barbell size={20} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="font-heading text-[15px] font-semibold leading-tight text-ink">
                HealthSpan
              </div>
              <div className="hs-label text-[10px]">Training</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          <NavItem to="/" icon={House} end>Home</NavItem>

          {(isAdmin || isCoach) && (
            <>
              <NavItem to="/people" icon={UsersThree}>People</NavItem>
              <NavItem to="/library" icon={BookOpen}>Library</NavItem>
              <NavItem to="/admin/packages" icon={CreditCard}>Class packs</NavItem>
              <NavItem to="/admin/revenue" icon={CurrencyDollar}>Revenue</NavItem>
              {isAdmin && <NavItem to="/admin/login-photos" icon={ImageSquare}>Sign-in photos</NavItem>}
              <button
                type="button"
                onClick={() => setBackupOpen(true)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors text-ink-2 hover:bg-muted2 hover:text-ink"
                data-testid="nav-backup"
              >
                <CloudArrowDown size={18} weight="regular" />
                <span>Backup & restore</span>
              </button>
              <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-ink-3">My training</div>
              <NavItem to="/week" icon={CalendarBlank}>My Week</NavItem>
              <NavItem to="/records" icon={Trophy}>My Records</NavItem>
              <NavItem to="/history" icon={ChartLine}>My History</NavItem>
              <NavItem to="/progress" icon={Camera}>Progress photos</NavItem>
              <NavItem to="/personal" icon={IdentificationCard}>Personal data</NavItem>
            </>
          )}

          {isAthlete && (
            <>
              <NavItem to="/week" icon={CalendarBlank}>My Week</NavItem>
              <NavItem to="/records" icon={Trophy}>Personal Records</NavItem>
              <NavItem to="/history" icon={ChartLine}>History</NavItem>
              <NavItem to="/progress" icon={Camera}>Progress photos</NavItem>
              <NavItem to="/personal" icon={IdentificationCard}>Personal data</NavItem>
              <NavItem to="/billing" icon={CreditCard}>My class packs</NavItem>
              <button
                type="button"
                onClick={() => setBackupOpen(true)}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors text-ink-2 hover:bg-muted2 hover:text-ink"
                data-testid="nav-backup"
              >
                <CloudArrowDown size={18} weight="regular" />
                <span>Backup my data</span>
              </button>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-line">
          <div className="mb-2"><OfflineBadge /></div>
          <div className="flex items-center gap-3 px-2 py-2 rounded-xl">
            <div className="h-9 w-9 rounded-full bg-sage grid place-items-center text-moss">
              <User size={16} weight="bold" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="hs-label text-[10px]">{user?.role}</div>
            </div>
            <button
              onClick={() => setProfileOpen(true)}
              aria-label="Edit profile"
              className="p-2 rounded-lg text-ink-3 hover:bg-muted2 hover:text-ink transition-colors"
              data-testid="open-profile-button"
            >
              <Gear size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden sticky top-0 z-40 bg-surface border-b border-line pl-20 pr-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-moss grid place-items-center">
              <Barbell size={16} weight="bold" className="text-white" />
            </div>
            <span className="font-heading font-semibold">HealthSpan</span>
          </Link>
          <div className="flex items-center gap-1">
            <OfflineBadge />
            {isAdmin && (
              <Link to="/admin/login-photos" className="p-2 rounded-lg text-ink-3 hover:bg-muted2 hover:text-ink"
                    aria-label="Sign-in photos" data-testid="mobile-login-photos-link">
                <ImageSquare size={18} />
              </Link>
            )}
            <button
              onClick={() => setProfileOpen(true)}
              className="p-2 rounded-lg text-ink-3 hover:bg-muted2 hover:text-ink"
              data-testid="mobile-open-profile-button"
              aria-label="Edit profile"
            >
              <Gear size={18} />
            </button>
          </div>
        </header>

        <main className="flex-1">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden sticky bottom-0 z-40 bg-surface border-t border-line flex items-center justify-around px-2 py-2">
          <NavLink to="/" end className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
            <House size={20} /> Home
          </NavLink>
          {(isAdmin || isCoach) && (
            <>
              <NavLink to="/people" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <UsersThree size={20} /> People
              </NavLink>
              <NavLink to="/week" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <CalendarBlank size={20} /> My Week
              </NavLink>
              <NavLink to="/library" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <BookOpen size={20} /> Library
              </NavLink>
              <NavLink to="/admin/packages" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <CreditCard size={20} /> Packs
              </NavLink>
            </>
          )}
          {isAthlete && (
            <>
              <NavLink to="/week" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <CalendarBlank size={20} /> Week
              </NavLink>
              <NavLink to="/records" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <Trophy size={20} /> PRs
              </NavLink>
              <NavLink to="/history" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <ChartLine size={20} /> History
              </NavLink>
              <NavLink to="/progress" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")} data-testid="mobile-nav-progress">
                <Camera size={20} /> Photos
              </NavLink>
              <NavLink to="/billing" className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[11px]", isActive ? "text-terracotta" : "text-ink-3")}>
                <CreditCard size={20} /> Billing
              </NavLink>
            </>
          )}
        </nav>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
      <BackupDialog open={backupOpen} onOpenChange={setBackupOpen} />
      <AlertDialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid="logout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Sign out of HealthSpan?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll need to log back in to resume your workout. Any unsaved timer progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="logout-cancel">Stay signed in</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLogout}
              className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
              data-testid="logout-confirm"
            >
              Sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Compact online/offline + sync-queue indicator. Tap to flush manually.
function OfflineBadge() {
  const [status, setStatus] = useState({ online: isOnline(), queued: queueLength() });
  useEffect(() => onStatusChange(setStatus), []);
  const { online, queued } = status;
  if (online && !queued) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-sage/60 text-moss text-[10px] font-semibold uppercase tracking-[0.12em]"
            title="Online · changes save instantly" data-testid="offline-badge">
        <CloudCheck size={11} weight="bold" /> Synced
      </span>
    );
  }
  if (!online) {
    return (
      <button
        type="button"
        onClick={() => flushQueue()}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-terracotta/15 text-terracotta text-[10px] font-semibold uppercase tracking-[0.12em] hover:bg-terracotta/25"
        title={`Offline${queued ? ` · ${queued} pending` : ""} — changes are saved on this device and will sync when you reconnect.`}
        data-testid="offline-badge"
      >
        <CloudSlash size={11} weight="bold" /> Offline{queued ? ` · ${queued}` : ""}
      </button>
    );
  }
  // online with a non-zero queue → background sync in progress
  return (
    <button
      type="button"
      onClick={() => flushQueue()}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-terracotta/15 text-terracotta text-[10px] font-semibold uppercase tracking-[0.12em] hover:bg-terracotta/25"
      title={`${queued} change${queued !== 1 ? "s" : ""} waiting to sync — tap to retry now.`}
      data-testid="offline-badge"
    >
      <ArrowsClockwise size={11} weight="bold" className="animate-spin-slow" /> Syncing · {queued}
    </button>
  );
}
